import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { taxCodesFor, useAccounts, useTaxCodes } from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { formatTrn } from '@/lib/clients';
import { COUNTRIES, KIND_LABEL, LIST_PATH, type Address, type Contact } from '@/lib/contacts';
import { useCurrencies, useHomeCurrency } from '@/lib/currencies';
import { useOrgId } from '@/lib/org';
import { EMIRATES, TERMS, useCompanySettings } from '@/lib/settings';
import { GST_STATES, gstinProblem } from '@/lib/gst';
import { homeCurrencyLabel, isIndia, orgLocale } from '@/lib/org-locale';

interface AddressForm {
  line1: string;
  line2: string;
  city: string;
  emirate: string;
  poBox: string;
  /** India: PIN code and GST state. */
  postalCode: string;
  state: string;
  country: string;
}

interface Form {
  kind: 'customer' | 'vendor';
  displayName: string;
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  mobile: string;
  website: string;
  billing: AddressForm;
  sameAsBilling: boolean;
  shipping: AddressForm;
  vatRegistered: boolean;
  trn: string;
  gstin: string;
  defaultTaxCodeId: string;
  paymentTermsDays: string;
  defaultAccountId: string;
  bankName: string;
  iban: string;
  notes: string;
  /** '' = home currency. */
  currency: string;
}

// a new contact is in the client's country: India clients default to India and their own state
const blankAddress = (): AddressForm => ({
  line1: '',
  line2: '',
  city: '',
  emirate: '',
  poBox: '',
  postalCode: '',
  state: isIndia() ? (orgLocale().stateCode ?? '') : '',
  country: isIndia() ? 'IN' : 'AE',
});
const fromAddress = (a: Address | null, state = ''): AddressForm =>
  a
    ? {
        line1: a.line1 ?? '',
        line2: a.line2 ?? '',
        city: a.city ?? '',
        emirate: a.emirate ?? '',
        poBox: a.poBox ?? '',
        postalCode: a.postalCode ?? '',
        state,
        country: a.country,
      }
    : { ...blankAddress(), state };
const sameAddress = (a: AddressForm, b: AddressForm) =>
  (Object.keys(a) as Array<keyof AddressForm>).every((k) => a[k] === b[k]);
const emptyAddress = (a: AddressForm) =>
  !a.line1 && !a.line2 && !a.city && !a.poBox && !a.emirate && !a.postalCode;

function initial(kind: 'customer' | 'vendor', terms: number, c?: Contact | null, name = ''): Form {
  if (!c)
    return {
      kind,
      displayName: name,
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      mobile: '',
      website: '',
      billing: blankAddress(),
      sameAsBilling: true,
      shipping: blankAddress(),
      vatRegistered: false,
      trn: '',
      gstin: '',
      defaultTaxCodeId: '',
      paymentTermsDays: String(terms),
      defaultAccountId: '',
      bankName: '',
      iban: '',
      notes: '',
      currency: '',
    };
  const billing = fromAddress(c.billingAddress, c.stateCode ?? '');
  const shipping = c.shippingAddress ? fromAddress(c.shippingAddress) : billing;
  return {
    kind,
    displayName: c.displayName,
    companyName: c.companyName ?? '',
    contactName: c.contactName ?? '',
    email: c.email ?? '',
    phone: c.phone ?? '',
    mobile: c.mobile ?? '',
    website: c.website ?? '',
    billing,
    sameAsBilling: !c.shippingAddress || sameAddress(billing, shipping),
    shipping,
    vatRegistered: c.vatRegistered,
    trn: c.trn ? formatTrn(c.trn) : '',
    gstin: c.gstin ?? '',
    defaultTaxCodeId: c.defaultTaxCodeId ?? '',
    paymentTermsDays: String(c.paymentTermsDays),
    defaultAccountId: c.defaultAccountId ?? '',
    bankName: c.bankName ?? '',
    iban: c.iban ?? '',
    notes: c.notes ?? '',
    currency: c.currency,
  };
}

/**
 * Customer / vendor form drawer (document 06 S11, E9; QBO's customer drawer): name and contact,
 * addresses, VAT, payments, notes. Opening balances come from the opening balance tool (epic 9).
 */
export function ContactDrawer({
  open,
  onOpenChange,
  kind,
  contact,
  initialName,
  chooseKind,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: 'customer' | 'vendor';
  contact?: Contact | null;
  initialName?: string;
  chooseKind?: boolean;
  onSaved?: (contact: Contact) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const settings = useCompanySettings();
  const currencies = useCurrencies();
  const homeFallback = useHomeCurrency();
  const home = homeFallback;
  const taxCodes = useTaxCodes();
  const accounts = useAccounts();
  const terms =
    kind === 'vendor'
      ? (settings.data?.expenses.defaultBillTermsDays ?? 30)
      : (settings.data?.sales.defaultTermsDays ?? 30);
  const [form, setForm] = useState<Form>(() => initial(kind, terms));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (which: 'billing' | 'shipping', k: keyof AddressForm, v: string) =>
    setForm((f) => ({ ...f, [which]: { ...f[which], [k]: v } }));

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(initial(kind, terms, contact, initialName));
    // terms only matter for a new contact; re-reading them must not reset a form being typed in
  }, [open, contact, kind, initialName]);

  const label = KIND_LABEL[form.kind];
  const side = form.kind === 'customer' ? 'sales' : 'purchases';
  const codes = taxCodesFor(taxCodes.data, side);
  const expenseAccounts = useMemo(
    () =>
      (accounts.data ?? []).filter(
        (a) => !a.isControl && (a.class === 'expense' || a.class === 'asset'),
      ),
    [accounts.data],
  );

  const save = useMutation({
    mutationFn: async () => {
      const trn = form.trn.replace(/[\s-]+/g, '');
      const abroad = form.billing.country !== 'AE';
      const inIndia = form.billing.country === 'IN';
      const gstin = form.gstin.replace(/\s+/g, '').toUpperCase();
      const address = (a: AddressForm) => ({
        line1: a.line1,
        line2: a.line2,
        city: a.city,
        emirate: a.country === 'AE' ? a.emirate : '',
        poBox: a.country === 'IN' ? '' : a.poBox,
        postalCode: a.country === 'IN' ? a.postalCode : '',
        country: a.country,
      });
      const shipping = form.sameAsBilling ? form.billing : form.shipping;
      const body = {
        ...(contact && !chooseKind ? {} : { kind: form.kind }),
        displayName: form.displayName.trim(),
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        phone: form.phone,
        mobile: form.mobile,
        website: form.website,
        country: form.billing.country,
        emirate: abroad ? null : form.billing.emirate,
        billingAddress: address(form.billing),
        shippingAddress: emptyAddress(shipping) ? null : address(shipping),
        ...(inIndia
          ? {
              gstin: gstin || null,
              stateCode: (gstin ? gstin.slice(0, 2) : form.billing.state) || null,
            }
          : {
              vatRegistered: abroad ? false : form.vatRegistered,
              trn: abroad ? null : trn || null,
              ...(isIndia() ? { gstin: null, stateCode: null } : {}),
            }),
        defaultTaxCodeId: form.defaultTaxCodeId || null,
        paymentTermsDays: Number(form.paymentTermsDays),
        ...(form.kind === 'vendor'
          ? {
              defaultAccountId: form.defaultAccountId || null,
              bankName: form.bankName,
              iban: form.iban,
            }
          : {}),
        notes: form.notes,
        // multi-currency: fixed once the contact has transactions (the API refuses a change)
        ...(form.currency && (!contact || !contact.hasTransactions)
          ? { currency: form.currency }
          : {}),
      };
      return contact
        ? api<Contact>(`/orgs/${orgId}/contacts/${contact.id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
          })
        : api<Contact>(`/orgs/${orgId}/contacts`, { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: async (saved) => {
      await qc.invalidateQueries({ queryKey: ['contacts', orgId] });
      toast(`${saved.displayName} saved.`, {
        link: {
          to: `${LIST_PATH[form.kind]}/${saved.id}`,
          label: `View ${KIND_LABEL[form.kind].one.toLowerCase()}`,
        },
      });
      onSaved?.(saved);
      onOpenChange(false);
    },
    onError: (e) => {
      if (e instanceof ApiError && Array.isArray(e.details)) {
        const next: Record<string, string> = {};
        for (const d of e.details as Array<{ path: string; message: string }>)
          next[d.path] = d.message;
        setErrors(next);
      }
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    const trn = form.trn.replace(/[\s-]+/g, '');
    if (!form.displayName.trim()) next['displayName'] = 'Enter a display name.';
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      next['email'] = 'Enter a valid email address.';
    if (form.billing.country === 'IN' && form.gstin.trim()) {
      const problem = gstinProblem(form.gstin);
      if (problem) next['gstin'] = problem;
    }
    if (form.billing.country === 'AE') {
      if (trn && !/^[0-9]{15}$/.test(trn)) next['trn'] = 'A TRN has 15 digits.';
      if (form.vatRegistered && !trn) next['trn'] = 'A VAT-registered contact needs its TRN.';
    }
    const iban = form.iban.replace(/[\s/:-]+/g, '').toUpperCase();
    const indianAccount = /^[0-9]{9,18}[A-Z]{4}0[A-Z0-9]{6}$/.test(iban);
    if (form.kind === 'vendor' && iban && isIndia() && !indianAccount)
      next['iban'] = 'Enter the account number and IFSC (for example 50200012345678 HDFC0000123).';
    else if (
      form.kind === 'vendor' &&
      iban &&
      !isIndia() &&
      !/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(iban)
    )
      next['iban'] = 'Enter a valid IBAN (for example AE07 0331 2345 6789 0123 456).';
    else if (form.kind === 'vendor' && /^AE[0-9]/.test(iban) && iban.length !== 23)
      next['iban'] = 'A UAE IBAN has 23 characters.';
    setErrors(next);
    if (!Object.keys(next).length) save.mutate();
  }

  const banner = save.error ? save.error.message : null;
  const err = (k: string) => errors[k];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[680px]">
        <form noValidate onSubmit={submit} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle>
              {contact ? `${label.one} information` : `New ${label.one.toLowerCase()}`}
            </SheetTitle>
            <SheetDescription>
              {contact
                ? contact.displayName
                : `Add a ${label.one.toLowerCase()} to use on transactions.`}
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-1 flex-col gap-5 overflow-y-auto px-4 pb-4">
            <ErrorBanner>{banner}</ErrorBanner>
            {chooseKind ? (
              <Field label="Type">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.kind}
                    onChange={(e) => set('kind', e.target.value as Form['kind'])}
                  >
                    <option value="customer">Customer</option>
                    <option value="vendor">{KIND_LABEL.vendor.one}</option>
                  </NativeSelect>
                )}
              </Field>
            ) : null}

            <Group title="Name and contact">
              <Field
                label={`${label.one} display name *`}
                className="col-span-2"
                error={err('displayName')}
              >
                {(id) => (
                  <Input
                    id={id}
                    value={form.displayName}
                    autoFocus
                    aria-invalid={Boolean(err('displayName'))}
                    onChange={(e) => set('displayName', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Company name">
                {(id) => (
                  <Input
                    id={id}
                    value={form.companyName}
                    onChange={(e) => set('companyName', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Contact name">
                {(id) => (
                  <Input
                    id={id}
                    value={form.contactName}
                    onChange={(e) => set('contactName', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Email" error={err('email')}>
                {(id) => (
                  <Input
                    id={id}
                    type="email"
                    value={form.email}
                    aria-invalid={Boolean(err('email'))}
                    onChange={(e) => set('email', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Phone number">
                {(id) => (
                  <Input
                    id={id}
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Mobile number">
                {(id) => (
                  <Input
                    id={id}
                    value={form.mobile}
                    onChange={(e) => set('mobile', e.target.value)}
                  />
                )}
              </Field>
              <Field label="Website">
                {(id) => (
                  <Input
                    id={id}
                    value={form.website}
                    onChange={(e) => set('website', e.target.value)}
                  />
                )}
              </Field>
            </Group>

            <Group title={form.kind === 'vendor' ? 'Address' : 'Addresses'}>
              <AddressFields
                prefix="Billing"
                value={form.billing}
                onChange={(k, v) => setAddr('billing', k, v)}
                emirateError={err('emirate')}
              />
              {form.kind === 'customer' ? (
                <>
                  <Checkbox
                    className="col-span-2"
                    label="Shipping address same as billing address"
                    checked={form.sameAsBilling}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        sameAsBilling: e.target.checked,
                        shipping: e.target.checked ? f.billing : f.shipping,
                      }))
                    }
                  />
                  {!form.sameAsBilling ? (
                    <AddressFields
                      prefix="Shipping"
                      value={form.shipping}
                      onChange={(k, v) => setAddr('shipping', k, v)}
                    />
                  ) : null}
                </>
              ) : null}
            </Group>

            <Group title={isIndia() ? 'GST' : 'VAT'}>
              {form.billing.country === 'IN' ? (
                <>
                  <Field
                    label="GSTIN"
                    hint="15 characters; leave blank for an unregistered person."
                    error={err('gstin')}
                    className="col-span-2"
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.gstin}
                        placeholder="29AAGCB7383J1Z4"
                        aria-invalid={Boolean(err('gstin'))}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase();
                          set('gstin', v);
                          const code = v.replace(/\s+/g, '').slice(0, 2);
                          if (GST_STATES.some(([c]) => c === code))
                            setAddr('billing', 'state', code);
                        }}
                      />
                    )}
                  </Field>
                </>
              ) : form.billing.country === 'AE' ? (
                <>
                  <Checkbox
                    className="col-span-2"
                    label={`${label.one} is registered for UAE VAT`}
                    checked={form.vatRegistered}
                    onChange={(e) => set('vatRegistered', e.target.checked)}
                  />
                  <Field
                    label="TRN"
                    hint="15 digits; required on tax invoices to VAT-registered customers."
                    error={err('trn')}
                  >
                    {(id) => (
                      <Input
                        id={id}
                        value={form.trn}
                        inputMode="numeric"
                        placeholder="100 1234567 00 003"
                        aria-invalid={Boolean(err('trn'))}
                        onChange={(e) => set('trn', e.target.value)}
                      />
                    )}
                  </Field>
                </>
              ) : (
                <p className="col-span-2 text-xs text-muted-foreground">
                  {isIndia()
                    ? 'Outside India: no GSTIN. Sales are exports (EXP-LUT); services bought from abroad use the reverse charge (RCMI).'
                    : 'Outside the UAE: no TRN. Purchases of services from abroad usually use the reverse charge (RC).'}
                </p>
              )}
              <Field
                label={isIndia() ? 'Default GST rate' : 'Default VAT treatment'}
                hint={`Used on new ${form.kind === 'customer' ? 'sales' : 'purchase'} lines.`}
                error={err('defaultTaxCodeId')}
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.defaultTaxCodeId}
                    onChange={(e) => set('defaultTaxCodeId', e.target.value)}
                  >
                    <option value="">From the product or account</option>
                    {codes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.code} · {t.name}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </Group>

            <Group title="Payments">
              <Field label="Terms">
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.paymentTermsDays}
                    onChange={(e) => set('paymentTermsDays', e.target.value)}
                  >
                    {TERMS.map((t) => (
                      <option key={t.days} value={t.days}>
                        {t.label}
                      </option>
                    ))}
                    {!TERMS.some((t) => String(t.days) === form.paymentTermsDays) ? (
                      <option value={form.paymentTermsDays}>Net {form.paymentTermsDays}</option>
                    ) : null}
                  </NativeSelect>
                )}
              </Field>
              <Field
                label="Currency"
                error={err('currency')}
                hint={
                  contact?.hasTransactions
                    ? `Fixed once the ${KIND_LABEL[form.kind].one.toLowerCase()} has transactions.`
                    : currencies.data && !currencies.data.enabled
                      ? 'Switch multicurrency on in Company settings › Advanced to use other currencies.'
                      : undefined
                }
              >
                {(id) => (
                  <NativeSelect
                    id={id}
                    value={form.currency || home}
                    disabled={
                      Boolean(contact?.hasTransactions) || !(currencies.data?.enabled ?? false)
                    }
                    onChange={(e) => set('currency', e.target.value)}
                  >
                    <option value={home}>{homeCurrencyLabel(home)} (home currency)</option>
                    {(currencies.data?.currencies ?? []).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code} – {c.name}
                      </option>
                    ))}
                    {form.currency &&
                    form.currency !== home &&
                    !currencies.data?.currencies.some((c) => c.code === form.currency) ? (
                      <option value={form.currency}>{form.currency}</option>
                    ) : null}
                  </NativeSelect>
                )}
              </Field>
              {form.kind === 'vendor' ? (
                <>
                  <Field
                    label="Default category"
                    className="col-span-2"
                    hint="The expense account bills from this vendor start with."
                    error={err('defaultAccountId')}
                  >
                    {(id) => (
                      <NativeSelect
                        id={id}
                        value={form.defaultAccountId}
                        onChange={(e) => set('defaultAccountId', e.target.value)}
                      >
                        <option value="">None</option>
                        {expenseAccounts.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.code} · {a.fullName}
                          </option>
                        ))}
                      </NativeSelect>
                    )}
                  </Field>
                  <Field label="Bank name">
                    {(id) => (
                      <Input
                        id={id}
                        value={form.bankName}
                        onChange={(e) => set('bankName', e.target.value)}
                      />
                    )}
                  </Field>
                  <Field label={isIndia() ? 'Account number and IFSC' : 'IBAN'} error={err('iban')}>
                    {(id) => (
                      <Input
                        id={id}
                        value={form.iban}
                        placeholder={
                          isIndia() ? '50200012345678 HDFC0000123' : 'AE07 0331 2345 6789 0123 456'
                        }
                        aria-invalid={Boolean(err('iban'))}
                        onChange={(e) => set('iban', e.target.value)}
                      />
                    )}
                  </Field>
                </>
              ) : null}
              <p className="col-span-2 text-xs text-muted-foreground">
                Opening balances are entered with the opening balance tool (coming soon), not on
                this form.
              </p>
            </Group>

            <Group title="Notes">
              <Field label="Notes" className="col-span-2">
                {(id) => (
                  <Textarea
                    id={id}
                    value={form.notes}
                    onChange={(e) => set('notes', e.target.value)}
                  />
                )}
              </Field>
            </Group>
          </div>
          <SheetFooter className="flex-row justify-end border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-2 text-sm font-semibold">{title}</legend>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

function AddressFields({
  prefix,
  value,
  onChange,
  emirateError,
}: {
  prefix: string;
  value: AddressForm;
  onChange: (k: keyof AddressForm, v: string) => void;
  emirateError?: string | undefined;
}) {
  return (
    <>
      <Field label={`${prefix} street address`} className="col-span-2">
        {(id) => (
          <Input id={id} value={value.line1} onChange={(e) => onChange('line1', e.target.value)} />
        )}
      </Field>
      <Field label={`${prefix} address line 2`} className="col-span-2">
        {(id) => (
          <Input id={id} value={value.line2} onChange={(e) => onChange('line2', e.target.value)} />
        )}
      </Field>
      <Field label={`${prefix} city`}>
        {(id) => (
          <Input id={id} value={value.city} onChange={(e) => onChange('city', e.target.value)} />
        )}
      </Field>
      {value.country === 'IN' ? (
        <Field label={`${prefix} PIN code`}>
          {(id) => (
            <Input
              id={id}
              value={value.postalCode}
              inputMode="numeric"
              onChange={(e) => onChange('postalCode', e.target.value)}
            />
          )}
        </Field>
      ) : (
        <Field label={`${prefix} P.O. Box`}>
          {(id) => (
            <Input
              id={id}
              value={value.poBox}
              onChange={(e) => onChange('poBox', e.target.value)}
            />
          )}
        </Field>
      )}
      <Field label={`${prefix} country`}>
        {(id) => (
          <NativeSelect
            id={id}
            value={value.country}
            onChange={(e) => onChange('country', e.target.value)}
          >
            {Object.entries(COUNTRIES).map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
            {!COUNTRIES[value.country] ? (
              <option value={value.country}>{value.country}</option>
            ) : null}
          </NativeSelect>
        )}
      </Field>
      {value.country === 'IN' && prefix === 'Billing' ? (
        <Field label={`${prefix} state`} hint="Default place of supply.">
          {(id) => (
            <NativeSelect
              id={id}
              value={value.state}
              onChange={(e) => onChange('state', e.target.value)}
            >
              <option value="">Not set</option>
              {GST_STATES.map(([code, name]) => (
                <option key={code} value={code}>
                  {code} · {name}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
      ) : value.country === 'AE' ? (
        <Field
          label={`${prefix} emirate`}
          hint={prefix === 'Billing' ? 'Default place of supply.' : undefined}
          error={emirateError}
        >
          {(id) => (
            <NativeSelect
              id={id}
              value={value.emirate}
              onChange={(e) => onChange('emirate', e.target.value)}
            >
              <option value="">Not set</option>
              {EMIRATES.map((em) => (
                <option key={em}>{em}</option>
              ))}
            </NativeSelect>
          )}
        </Field>
      ) : (
        <div />
      )}
    </>
  );
}
